import {
  Button,
  Flex,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  useColorModeValue,
  Box,
  HStack,
  VStack,
  Icon,
  Badge,
  Input,
  InputGroup,
  InputLeftElement,
  Select,
  IconButton,
  Tooltip,
  TableContainer,
} from "@chakra-ui/react";
import React, { useEffect, useMemo, useState } from "react";
import {
  useGlobalFilter,
  usePagination,
  useSortBy,
  useTable,
} from "react-table";
import { motion } from "framer-motion";
import {
  SearchIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  DeleteIcon,
  CheckCircleIcon,
  ChevronDownIcon,
  ViewIcon,
  InfoIcon,
} from "@chakra-ui/icons";

// Custom components
import Card from "components/card/Card";
import { useRecoilState } from "recoil";
import DeleteModal from "components/modals/DeleteModal";
import { deleteModalState } from "atoms/deleteModalState";
import useAxios from "axios-hooks";
import { updatePartnerDrawerState } from "atoms/updatePartnerDrawerState";
import { createPartnerDrawerState } from "atoms/createPartnerDrawerState";
import UpdatePartnerDrawer from "./UpdatePartnerDrawer";
import CreatePartnerDrawer from "./CreatePartnerDrawer";
import { useNavigate } from "react-router-dom";
import logger from "infra/logger";
import LoadingSpinner from "components/feedback/LoadingSpinner";

// Motion components
const MotionBox = motion(Box);
const MotionFlex = motion(Flex);
const MotionTr = motion(Tr);

// Styled components
const GlassMorphismCard = ({ children, ...rest }) => {
  const bgColor = useColorModeValue(
    "rgba(255, 255, 255, 0.8)",
    "rgba(26, 32, 44, 0.8)"
  );
  const borderColor = useColorModeValue(
    "rgba(255, 255, 255, 0.5)",
    "rgba(255, 255, 255, 0.1)"
  );

  return (
    <Box
      bg={bgColor}
      backdropFilter="blur(10px)"
      borderRadius="2xl"
      boxShadow="xl"
      border="1px solid"
      borderColor={borderColor}
      overflow="hidden"
      transition="all 0.3s ease"
      _hover={{ boxShadow: "2xl" }}
      {...rest}
    >
      {children}
    </Box>
  );
};

const EmptyStateIllustration = () => (
  <MotionFlex
    direction="column"
    align="center"
    justify="center"
    py={12}
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.5 }}
  >
    <Icon as={CheckCircleIcon} boxSize={16} color="brand.500" mb={4} />
    <Text fontSize="xl" fontWeight="bold" mb={2}>
      No Partners Found
    </Text>
    <Text color="gray.500" textAlign="center" maxW="md">
      There are no partners available at the moment. New partners will appear
      here once added.
    </Text>
  </MotionFlex>
);

const StatusBadge = ({ isActive }) => {
  return (
    <Badge
      px={3}
      py={1}
      borderRadius="full"
      bg={isActive ? "green.400" : "red.400"}
      color="white"
      fontSize="xs"
      fontWeight="medium"
    >
      {isActive ? "Active" : "Inactive"}
    </Badge>
  );
};

const PaginationButton = ({ children, isActive, isDisabled, onClick }) => {
  return (
    <Button
      size="sm"
      variant={isActive ? "solid" : "outline"}
      colorScheme={isActive ? "brand" : "gray"}
      isDisabled={isDisabled}
      onClick={onClick}
      minW="40px"
      h="40px"
      borderRadius="md"
      _hover={!isDisabled && { transform: "translateY(-2px)", shadow: "md" }}
      transition="all 0.2s"
    >
      {children}
    </Button>
  );
};

const PartnersColumnsTable = (props) => {
  const { columnsData } = props;
  const [showDeleteModal, setShowDeleteModal] =
    useRecoilState(deleteModalState);
  const [selectedPartner, setSelectedPartner] = useState(null);
  const [partners, setPartners] = useState([]);
  const [updateDrawerState, setUpdateDrawerState] = useRecoilState(
    updatePartnerDrawerState
  );
  const [createDrawerState, setCreateDrawerState] = useRecoilState(
    createPartnerDrawerState
  );
  const [searchQuery, setSearchQuery] = useState("");
  const [filteredPartners, setFilteredPartners] = useState([]);
  const [pageSize, setPageSize] = useState(8);

  const navigate = useNavigate();
  const [{ data: partnerData, loading, error }, fetchPartners] = useAxios(
    { method: "GET", url: `/api/partner` },
    { manual: true }
  );

  const themeColor = "#23A979";
  const textColor = useColorModeValue("#2D3748", "white");
  const borderColor = useColorModeValue("gray.200", "whiteAlpha.100");
  const hoverBg = useColorModeValue("#F7FAFC", "#2D3748");
  const theadBgColor = useColorModeValue("#F7FAFC", "#1A202C");

  // Fetch partners on component mount
  useEffect(() => {
    getPartners();
  }, []);

  // Filter partners based on search query
  useEffect(() => {
    if (searchQuery.trim() === "") {
      setFilteredPartners(partners);
    } else {
      const lowercasedQuery = searchQuery.toLowerCase();
      const filtered = partners.filter(
        (partner) =>
          partner.Name?.toLowerCase().includes(lowercasedQuery) ||
          partner.UserName?.toLowerCase().includes(lowercasedQuery) ||
          partner.Email?.toLowerCase().includes(lowercasedQuery) ||
          partner.MobileNumber?.toLowerCase().includes(lowercasedQuery)
      );
      setFilteredPartners(filtered);
    }
  }, [searchQuery, partners]);

  // Define columns
  const columns = useMemo(() => columnsData, [columnsData]);
  const tableData = useMemo(() => filteredPartners, [filteredPartners]);

  // Initialize react-table
  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    prepareRow,
    page,
    canPreviousPage,
    canNextPage,
    pageCount,
    gotoPage,
    nextPage,
    previousPage,
    setPageSize: setTablePageSize,
    state: { pageIndex },
  } = useTable(
    {
      columns,
      data: tableData,
      initialState: {
        pageIndex: 0,
        pageSize,
      },
    },
    useGlobalFilter,
    useSortBy,
    usePagination
  );

  // Update react-table pageSize when local pageSize changes
  useEffect(() => {
    setTablePageSize(pageSize);
  }, [pageSize, setTablePageSize]);

  // Event handlers
  const editHandler = (id) => {
    setUpdateDrawerState({ isOpen: true, activeId: id });
  };

  const getPartners = async () => {
    try {
      const response = await fetchPartners();
      setPartners(response.data.data);
      setFilteredPartners(response.data.data);
    } catch (error) {
      logger.log(error);
    }
  };

  const handleDeleteClick = (partner) => {
    setSelectedPartner(partner);
    setShowDeleteModal(true);
  };

  const updateHandler = () => {
    setUpdateDrawerState(null);
    setCreateDrawerState(null);
    getPartners();
  };

  const handleCancelDelete = () => {
    setSelectedPartner(null);
    setShowDeleteModal(false);
  };

  const handleConfirmDelete = () => {
    setShowDeleteModal(false);
    getPartners();
  };

  const handleSearchChange = (e) => {
    setSearchQuery(e.target.value);
  };

  const handlePageSizeChange = (e) => {
    setPageSize(Number(e.target.value));
  };

  if (loading) {
    return <LoadingSpinner />;
  }

  return (
    <MotionBox
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
    >
      <GlassMorphismCard>
        {/* Header with search and title */}
        <Flex
          p={6}
          direction={{ base: "column", md: "row" }}
          justify="space-between"
          align={{ base: "start", md: "center" }}
          wrap="wrap"
          gap={4}
        >
          {/* Search input */}
          <HStack spacing={4} flex={{ base: "1", md: "2" }} flexWrap="wrap">
            <VStack align="start" spacing={1} minW="250px">
              <Text fontSize="xs" fontWeight="medium" color="gray.500">
                Search Partners
              </Text>
              <InputGroup size="sm">
                <InputLeftElement pointerEvents="none">
                  <SearchIcon color="gray.300" />
                </InputLeftElement>
                <Input
                  value={searchQuery}
                  onChange={handleSearchChange}
                  placeholder="Search by name, username, email or mobile"
                  borderRadius="md"
                />
              </InputGroup>
              <Text fontSize="xs" color="gray.500" mt={1} ml={1}>
                <Icon as={InfoIcon} boxSize={3} mr={1} />
                Type any keyword to search across all fields
              </Text>
            </VStack>
          </HStack>

          {/* Title */}
          <HStack spacing={2} justify="flex-end">
            <Text fontSize="xl" fontWeight="bold">
              Partners
            </Text>
            <Badge
              colorScheme="brand"
              fontSize="sm"
              py={1}
              px={2}
              borderRadius="md"
            >
              {filteredPartners.length}
            </Badge>
          </HStack>
        </Flex>

        {/* Table */}
        <TableContainer>
          <Table variant="simple" color="gray.500" {...getTableProps()}>
            <Thead bg={theadBgColor}>
              {headerGroups.map((headerGroup, index) => (
                <Tr {...headerGroup.getHeaderGroupProps()} key={index}>
                  {headerGroup.headers.map((column, index) => (
                    <Th
                      {...column.getHeaderProps(column.getSortByToggleProps())}
                      key={index}
                      py={4}
                      px={6}
                      borderColor={borderColor}
                      textTransform="capitalize"
                      fontSize="xs"
                      fontWeight="bold"
                      letterSpacing="wider"
                    >
                      <Flex
                        justify="center"
                        align="center"
                        fontSize={{ sm: "10px", lg: "12px" }}
                        color="gray.400"
                      >
                        {column.render("Header")}
                        {column.isSorted ? (
                          column.isSortedDesc ? (
                            <Icon as={ChevronDownIcon} ml={1} boxSize={4} />
                          ) : (
                            <Icon
                              as={ChevronRightIcon}
                              ml={1}
                              boxSize={4}
                              transform="rotate(-90deg)"
                            />
                          )
                        ) : null}
                      </Flex>
                    </Th>
                  ))}
                </Tr>
              ))}
            </Thead>
            <Tbody {...getTableBodyProps()}>
              {page.map((row, index) => {
                prepareRow(row);
                return (
                  <MotionTr
                    {...row.getRowProps()}
                    key={index}
                    whileHover={{ backgroundColor: hoverBg }}
                    transition={{ duration: 0.2 }}
                  >
                    <Td
                      textAlign="center"
                      py={4}
                      px={6}
                      borderColor={borderColor}
                    >
                      <Text color={textColor} fontSize="sm" fontWeight="600">
                        {pageIndex * pageSize + index + 1}
                      </Text>
                    </Td>
                    <Td
                      textAlign="center"
                      py={4}
                      px={6}
                      borderColor={borderColor}
                    >
                      <Text color={textColor} fontSize="sm" fontWeight="600">
                        {row.original.Name}
                      </Text>
                    </Td>
                    <Td
                      textAlign="center"
                      py={4}
                      px={6}
                      borderColor={borderColor}
                    >
                      <Text color={textColor} fontSize="sm" fontWeight="600">
                        {row.original.UserName}
                      </Text>
                    </Td>
                    <Td
                      textAlign="center"
                      py={4}
                      px={6}
                      borderColor={borderColor}
                    >
                      <Text color={textColor} fontSize="sm" fontWeight="600">
                        {row.original.Email}
                      </Text>
                    </Td>
                    <Td
                      textAlign="center"
                      py={4}
                      px={6}
                      borderColor={borderColor}
                    >
                      <Text color={textColor} fontSize="sm" fontWeight="600">
                        {row.original.MobileNumber}
                      </Text>
                    </Td>
                    <Td
                      textAlign="center"
                      py={4}
                      px={6}
                      borderColor={borderColor}
                    >
                      <Flex align="center" justify="center">
                        <StatusBadge isActive={row.original.IsActive} />
                      </Flex>
                    </Td>
                    <Td
                      textAlign="center"
                      py={4}
                      px={6}
                      borderColor={borderColor}
                    >
                      <Flex align="center" justify="center" gap={2}>
                        <Tooltip label="View Partner" placement="top">
                          <IconButton
                            icon={<ViewIcon />}
                            colorScheme="blue"
                            variant="ghost"
                            size="sm"
                            onClick={() => editHandler(row.original.Id)}
                            borderRadius="full"
                            aria-label="View Partner"
                          />
                        </Tooltip>
                        <Tooltip label="Delete Partner" placement="top">
                          <IconButton
                            icon={<DeleteIcon />}
                            colorScheme="red"
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDeleteClick(row.original)}
                            borderRadius="full"
                            aria-label="Delete Partner"
                          />
                        </Tooltip>
                      </Flex>
                    </Td>
                  </MotionTr>
                );
              })}
            </Tbody>
          </Table>
        </TableContainer>

        {/* Empty state */}
        {page.length === 0 && <EmptyStateIllustration />}

        {/* Pagination */}
        <Flex
          p={4}
          justify="space-between"
          align="center"
          borderTop="1px solid"
          borderColor={borderColor}
          flexWrap="wrap"
          gap={4}
        >
          <HStack>
            <Select
              value={pageSize}
              onChange={handlePageSizeChange}
              size="sm"
              borderRadius="md"
              w="120px"
            >
              {[5, 8, 10, 20, 30].map((size) => (
                <option key={size} value={size}>
                  Show {size}
                </option>
              ))}
            </Select>
            <Text fontSize="sm" color="gray.500">
              Showing {page.length} of {filteredPartners.length} results
            </Text>
          </HStack>

          <HStack spacing={2}>
            <PaginationButton
              isDisabled={!canPreviousPage}
              onClick={() => gotoPage(0)}
            >
              <ChevronLeftIcon boxSize={4} mr={1} />
              First
            </PaginationButton>

            <PaginationButton
              isDisabled={!canPreviousPage}
              onClick={() => previousPage()}
            >
              <ChevronLeftIcon boxSize={4} />
            </PaginationButton>

            {pageCount <= 5 ? (
              // If we have 5 or fewer pages, show all page buttons
              [...Array(pageCount)].map((_, i) => (
                <PaginationButton
                  key={i}
                  isActive={pageIndex === i}
                  onClick={() => gotoPage(i)}
                >
                  {i + 1}
                </PaginationButton>
              ))
            ) : (
              // If we have more than 5 pages, show a subset
              <>
                {pageIndex > 1 && (
                  <PaginationButton onClick={() => gotoPage(0)}>
                    1
                  </PaginationButton>
                )}

                {pageIndex > 2 && <Text>...</Text>}

                {pageIndex > 0 && (
                  <PaginationButton onClick={() => gotoPage(pageIndex - 1)}>
                    {pageIndex}
                  </PaginationButton>
                )}

                <PaginationButton isActive onClick={() => gotoPage(pageIndex)}>
                  {pageIndex + 1}
                </PaginationButton>

                {pageIndex < pageCount - 1 && (
                  <PaginationButton onClick={() => gotoPage(pageIndex + 1)}>
                    {pageIndex + 2}
                  </PaginationButton>
                )}

                {pageIndex < pageCount - 3 && <Text>...</Text>}

                {pageIndex < pageCount - 2 && (
                  <PaginationButton onClick={() => gotoPage(pageCount - 1)}>
                    {pageCount}
                  </PaginationButton>
                )}
              </>
            )}

            <PaginationButton
              isDisabled={!canNextPage}
              onClick={() => nextPage()}
            >
              <ChevronRightIcon boxSize={4} />
            </PaginationButton>

            <PaginationButton
              isDisabled={!canNextPage}
              onClick={() => gotoPage(pageCount - 1)}
            >
              Last
              <ChevronRightIcon boxSize={4} ml={1} />
            </PaginationButton>
          </HStack>
        </Flex>
      </GlassMorphismCard>

      {/* Modals and Drawers */}
      <DeleteModal
        isOpen={showDeleteModal}
        onClose={handleCancelDelete}
        onConfirm={handleConfirmDelete}
        data={selectedPartner}
        title="partner"
      />
      {createDrawerState?.isOpen && (
        <CreatePartnerDrawer update={updateHandler} />
      )}
      {updateDrawerState?.isOpen && (
        <UpdatePartnerDrawer update={updateHandler} />
      )}
    </MotionBox>
  );
};

export default PartnersColumnsTable;
